import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  confirmUpload,
  getPresignedUrl,
  putToS3WithProgress,
} from "../../api/projects";
import { projectsQueryKeys } from "../../hooks/useProjects";

/**
 * Stage 8.6 multi-file upload modal.
 *
 * Industry-standard pattern:
 * - HTML5 ``<input type="file" multiple>`` + drag-and-drop on the
 *   modal body.
 * - Each selected file becomes a row showing name / size / status.
 * - Up to ``MAX_CONCURRENCY`` uploads run in parallel.
 * - Each file follows the three-call protocol (presign → S3 PUT →
 *   confirm) independently, so a single bad file doesn't block the
 *   rest.
 * - The modal stays open after the batch finishes so the user can
 *   review which files succeeded and which failed before dismissing.
 *
 * No pre-upload classification UI — files always upload as ``generic``
 * and the existing classification card / Files-tab badge handles
 * routing post-upload (Stage 8.6 Item 1).
 */

interface Props {
  projectId: string;
  onClose: () => void;
  /**
   * Per-file progress callback consumed by the Files tab so the table
   * row can render "Uploading… X%" / "Analyzing…" badges while the
   * file is in flight. Optional — modal works fine standalone.
   */
  onFileProgress?: (update: ModalFileUpdate) => void;
}

export type ModalFileStatus =
  | "queued"
  | "presigning"
  | "uploading"
  | "confirming"
  | "uploaded"
  | "failed";

export interface ModalFileUpdate {
  /** Local row id; stable for the lifetime of the modal. */
  localId: string;
  /** Once presign completes, this is the real backend layer_id. */
  layerId: string | null;
  /** Original file metadata. */
  filename: string;
  sizeBytes: number;
  status: ModalFileStatus;
  /** Fraction 0..1 for the S3 PUT step; otherwise undefined. */
  progress?: number;
  errorMessage?: string | null;
}

interface RowState extends ModalFileUpdate {
  /** Kept off the wire callback — File object reference for the
   * in-flight upload. */
  file: File;
}

const ACCEPTED_EXTENSIONS = [".kml", ".kmz", ".geojson", ".json", ".pdf"];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(",");
const MAX_CONCURRENCY = 4;

interface BackendErrorBody {
  detail?: { code?: string; message?: string } | string;
}

function hasAcceptedExtension(name: string): boolean {
  return ACCEPTED_EXTENSIONS.some((ext) =>
    name.toLowerCase().endsWith(ext),
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function extractBackendError(
  err: unknown,
  step: ModalFileStatus,
): string {
  const e = err as { response?: { data?: BackendErrorBody }; message?: string };
  if (step === "uploading") {
    return (
      `${e.message || "S3 upload failed."} — check that the projects ` +
      "bucket is reachable from the browser."
    );
  }
  const body = e.response?.data;
  if (body && typeof body.detail === "object" && body.detail !== null) {
    const code = body.detail.code;
    const message = body.detail.message ?? "Upload failed.";
    if (code === "bounds_out_of_range") {
      return "File coordinates fall outside WGS84 range (±180°/±90°).";
    }
    if (code === "invalid_file_type") return "Unsupported file type.";
    if (code === "parse_failed") return `Parse failed: ${message}`;
    if (code === "upload_missing") {
      return "Upload did not land in storage — check MinIO / S3 CORS.";
    }
    return message;
  }
  if (body && typeof body.detail === "string") return body.detail;
  return e.message || "Upload failed.";
}

let _localIdCounter = 0;
function nextLocalId(): string {
  _localIdCounter += 1;
  return `upload-${Date.now().toString(36)}-${_localIdCounter}`;
}

export function FileUploadModal({
  projectId,
  onClose,
  onFileProgress,
}: Props) {
  const [rows, setRows] = useState<RowState[]>([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  // Stable reference to the progress callback so the per-file worker
  // doesn't re-create itself when the parent re-renders.
  const progressCallbackRef = useRef(onFileProgress);
  useEffect(() => {
    progressCallbackRef.current = onFileProgress;
  }, [onFileProgress]);

  const updateRow = useCallback(
    (localId: string, patch: Partial<RowState>) => {
      setRows((prev) => {
        const next = prev.map((r) =>
          r.localId === localId ? { ...r, ...patch } : r,
        );
        const found = next.find((r) => r.localId === localId);
        if (found) {
          progressCallbackRef.current?.({
            localId: found.localId,
            layerId: found.layerId,
            filename: found.filename,
            sizeBytes: found.sizeBytes,
            status: found.status,
            progress: found.progress,
            errorMessage: found.errorMessage,
          });
        }
        return next;
      });
    },
    [],
  );

  const onFilesPicked = useCallback(
    (files: FileList | File[] | null) => {
      if (!files) return;
      const arr = Array.from(files);
      const ok: File[] = [];
      const bad: string[] = [];
      for (const f of arr) {
        if (hasAcceptedExtension(f.name)) ok.push(f);
        else bad.push(f.name);
      }
      if (bad.length > 0) setRejected(bad);
      if (ok.length === 0) return;
      setRows((prev) => [
        ...prev,
        ...ok.map<RowState>((f) => ({
          localId: nextLocalId(),
          layerId: null,
          file: f,
          filename: f.name,
          sizeBytes: f.size,
          status: "queued",
          progress: 0,
          errorMessage: null,
        })),
      ]);
    },
    [],
  );

  const queuedCount = rows.filter((r) => r.status === "queued").length;
  const inFlightCount = rows.filter((r) =>
    ["presigning", "uploading", "confirming"].includes(r.status),
  ).length;
  const finishedCount = rows.filter((r) =>
    ["uploaded", "failed"].includes(r.status),
  ).length;
  const allDone = rows.length > 0 && finishedCount === rows.length;
  const canStart = queuedCount > 0 && !batchRunning;

  const startUploadFor = useCallback(
    async (row: RowState): Promise<void> => {
      const { localId, file } = row;
      let layerId: string | null = null;
      try {
        updateRow(localId, { status: "presigning", progress: 0 });
        const presign = await getPresignedUrl(projectId, file.name, "generic");
        layerId = presign.layer_id;
        // Update layerId AND status together so the Files-tab consumer
        // gets both at once.
        updateRow(localId, {
          layerId,
          status: "uploading",
          progress: 0.05,
        });
        // Invalidate the layers list now that the DB row exists, so
        // the Files-tab table renders the new row immediately with
        // the "Uploading…" badge.
        void qc.invalidateQueries({
          queryKey: projectsQueryKeys.layers(projectId),
        });

        await putToS3WithProgress(presign.presigned_url, file, (frac) => {
          updateRow(localId, { progress: 0.05 + frac * 0.85 });
        });
        updateRow(localId, { status: "confirming", progress: 0.95 });

        await confirmUpload(projectId, layerId, "generic");
        updateRow(localId, { status: "uploaded", progress: 1.0 });
        // Final invalidate so the row picks up classification updates.
        void qc.invalidateQueries({
          queryKey: projectsQueryKeys.layers(projectId),
        });
        void qc.invalidateQueries({
          queryKey: projectsQueryKeys.boundary(projectId),
        });
        void qc.invalidateQueries({
          queryKey: projectsQueryKeys.project(projectId),
        });
      } catch (err) {
        const step: ModalFileStatus =
          row.status === "queued" ? "presigning" : row.status;
        const message = extractBackendError(err, step);
        updateRow(localId, {
          status: "failed",
          errorMessage: message,
          layerId,
        });
      }
    },
    [projectId, qc, updateRow],
  );

  const startBatch = useCallback(async () => {
    if (queuedCount === 0 || batchRunning) return;
    setBatchRunning(true);
    // Snapshot the queue at the moment Start is pressed. Any file
    // that finished or failed earlier stays untouched; only currently
    // queued rows are dispatched.
    const queue = rows.filter((r) => r.status === "queued");
    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (cursor < queue.length) {
        const next = queue[cursor];
        cursor += 1;
        if (!next) return;
        await startUploadFor(next);
      }
    };
    const workers = Array.from(
      { length: Math.min(MAX_CONCURRENCY, queue.length) },
      () => worker(),
    );
    await Promise.all(workers);
    setBatchRunning(false);
  }, [batchRunning, queuedCount, rows, startUploadFor]);

  // Drag-and-drop handlers on the modal body. ``dragover`` MUST call
  // ``preventDefault`` to allow the drop event to fire.
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isDragOver) setIsDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer?.files?.length) {
      onFilesPicked(e.dataTransfer.files);
    }
  };

  const closeButtonLabel = useMemo(() => {
    if (rows.length === 0) return "Cancel";
    if (batchRunning) return "Working…";
    if (allDone) return "Done";
    return "Close";
  }, [rows.length, batchRunning, allDone]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-title"
      data-testid="file-upload-modal"
      style={backdropStyle}
    >
      <div
        style={modalCardStyle}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid="file-upload-dropzone"
      >
        <h2 id="upload-title" style={{ marginTop: 0 }}>
          Upload files
        </h2>
        <p style={{ color: "#475569", marginTop: 0 }}>
          Accepted: <code>{ACCEPTED_EXTENSIONS.join(", ")}</code>. Drop
          files anywhere on this dialog or use the picker. Selected
          files will be uploaded in parallel.
        </p>

        <div
          style={{
            ...dropZoneStyle,
            ...(isDragOver ? dropZoneHotStyle : {}),
          }}
        >
          <p style={{ margin: 0, color: "#475569" }}>
            Drag files here, or
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            data-testid="file-input"
            style={{ display: "none" }}
            onChange={(e) => {
              onFilesPicked(e.target.files);
              // Reset so the same file can be re-picked after removal.
              if (e.target) e.target.value = "";
            }}
          />
          <button
            type="button"
            data-testid="file-upload-pick"
            onClick={() => inputRef.current?.click()}
            style={pickButtonStyle}
          >
            Choose files
          </button>
        </div>

        {rejected.length > 0 && (
          <div
            role="alert"
            data-testid="upload-rejected"
            style={rejectedAlertStyle}
          >
            Rejected (unsupported type):{" "}
            <strong>{rejected.join(", ")}</strong>
          </div>
        )}

        {rows.length > 0 && (
          <ul
            data-testid="upload-rows"
            style={{ margin: "1rem 0 0", padding: 0, listStyle: "none" }}
          >
            {rows.map((row) => (
              <RowItem key={row.localId} row={row} />
            ))}
          </ul>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.5rem",
            marginTop: "1rem",
          }}
        >
          <button
            type="button"
            data-testid="upload-cancel"
            onClick={onClose}
            disabled={batchRunning}
            style={secondaryBtn}
          >
            {closeButtonLabel}
          </button>
          <button
            type="button"
            data-testid="upload-start"
            onClick={() => void startBatch()}
            disabled={!canStart}
            style={canStart ? primaryBtn : primaryBtnDisabled}
          >
            {batchRunning
              ? `Uploading ${inFlightCount}…`
              : queuedCount === 0
                ? "Start"
                : `Start (${queuedCount})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function RowItem({ row }: { row: RowState }) {
  const pctText =
    row.status === "uploading" && typeof row.progress === "number"
      ? `${Math.round(row.progress * 100)}%`
      : null;
  const statusText = (() => {
    switch (row.status) {
      case "queued":
        return "Queued";
      case "presigning":
        return "Requesting URL…";
      case "uploading":
        return `Uploading${pctText ? ` ${pctText}` : "…"}`;
      case "confirming":
        return "Processing…";
      case "uploaded":
        return "Uploaded";
      case "failed":
        return `Failed${row.errorMessage ? ` — ${row.errorMessage}` : ""}`;
    }
  })();

  const statusColor = (() => {
    switch (row.status) {
      case "uploaded":
        return "#15803d";
      case "failed":
        return "#b91c1c";
      default:
        return "#475569";
    }
  })();

  const showSpinner =
    row.status === "presigning" ||
    row.status === "uploading" ||
    row.status === "confirming";

  return (
    <li
      data-testid={`upload-row-${row.localId}`}
      data-status={row.status}
      style={{
        padding: "0.5rem 0.75rem",
        borderBottom: "1px solid #f3f4f6",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: "0.25rem 0.75rem",
        fontSize: "0.85rem",
      }}
    >
      <span
        style={{
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={row.filename}
      >
        {row.filename}
      </span>
      <span style={{ color: "#94a3b8" }}>{formatBytes(row.sizeBytes)}</span>
      <span
        style={{
          color: statusColor,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          gridColumn: "1 / span 2",
        }}
        data-testid={`upload-row-${row.localId}-status`}
      >
        {showSpinner && <Spinner />}
        {statusText}
      </span>
      {row.status === "uploading" && (
        <progress
          data-testid={`upload-row-${row.localId}-progress`}
          value={Math.round((row.progress ?? 0) * 100)}
          max={100}
          style={{
            width: "100%",
            gridColumn: "1 / span 2",
            height: 4,
            accentColor: "#635bff",
          }}
        />
      )}
    </li>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        border: "2px solid #c7d2fe",
        borderTopColor: "#4338ca",
        borderRadius: "50%",
        animation: "spark-spin 0.8s linear infinite",
      }}
    />
  );
}

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
};

const modalCardStyle: React.CSSProperties = {
  background: "white",
  padding: "1.5rem",
  borderRadius: 10,
  width: "min(560px, 95vw)",
  maxHeight: "85vh",
  overflowY: "auto",
};

const dropZoneStyle: React.CSSProperties = {
  marginTop: "1rem",
  border: "2px dashed #c7d2fe",
  borderRadius: 10,
  background: "#f9fafb",
  padding: "1.5rem",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.5rem",
  transition: "background 120ms ease, border-color 120ms ease",
};

const dropZoneHotStyle: React.CSSProperties = {
  background: "#eef2ff",
  borderColor: "#635bff",
};

const pickButtonStyle: React.CSSProperties = {
  background: "#635bff",
  color: "white",
  border: "none",
  borderRadius: 6,
  padding: "0.5rem 0.9rem",
  cursor: "pointer",
  fontWeight: 500,
};

const rejectedAlertStyle: React.CSSProperties = {
  marginTop: "1rem",
  background: "#fff7ed",
  color: "#9a3412",
  border: "1px solid #fed7aa",
  borderRadius: 6,
  padding: "0.5rem 0.75rem",
  fontSize: "0.85rem",
};

const primaryBtn: React.CSSProperties = {
  background: "#635bff",
  color: "white",
  border: "none",
  borderRadius: 6,
  padding: "0.55rem 1rem",
  cursor: "pointer",
  fontWeight: 500,
};

const primaryBtnDisabled: React.CSSProperties = {
  ...primaryBtn,
  background: "#c7d2fe",
  cursor: "not-allowed",
};

const secondaryBtn: React.CSSProperties = {
  background: "white",
  color: "#0a0e27",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: "0.55rem 1rem",
  cursor: "pointer",
};
